import React, { createRef, useRef, useState, createContext, useEffect } from 'react';
import useFocusManager from '@restart/hooks/useFocusManager';
import useMeasure from 'react-use-measure';
import ResizeObserver from 'resize-observer-polyfill';
import { position } from 'caret-pos';
import _merge from 'lodash/merge';
import _omit from 'lodash/omit';
import _uniqueId from 'lodash/uniqueId';
import { isFirefox } from 'react-device-detect';

import { SPACING_4 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import CloseIcon from '@nokia-csf-uxr/ccfk-assets/CloseIcon';
import { getContext, useCombinedRefs } from 'common';
import useDidUpdateEffect from 'common/useDidUpdateEffect';
import KEY_CODES from 'common/KeyCodes';
import Chip, { ChipLabel, ChipIconButton } from 'components/Chip';
import { IconButtonProps } from 'components/IconButton';

import {
  TextwChipsCore,
  TextwChipsInput,
  TextwChipsHighlighter,
  TextwChipsContentEditable,
  TextwChipsList,
  TextwChipsListProps
} from 'components/TextwChips';
import { MenuProps } from 'components/Menu';

const DEFAULT_RESIZE_MODE = 'none';

const {
  ENTER_KEY,
  SPACE_KEY,
  ESCAPE,
  DELETE,
  BACKSPACE,
  ARROW_DOWN,
  ARROW_LEFT,
  ARROW_RIGHT,
} = KEY_CODES;

const TextwChipsContext = createContext({});

const getIndexById: (any, string) => number = (data, id) => data.findIndex(item => item.id === id);

const EMPTY_STRING = '';
const BLANK_STRING = ' ';
const BLANK_ASCII = 160;
const SINGLE_LINE_HEIGHT = '2.25rem';

const CHIP_CONFIG = {
  role: 'cell'
};

export interface TextwChipsProps extends React.ComponentPropsWithRef<'div'> {
  /**
     * The data of the component (array of 'chip' and 'field' data items).
     *
     * `id`: The unique id of the item.
     *
     * `value`: The value of the item.
     * `placeholder`: The placeholder of teh field.
     * `type`: The type of the item.
     *  If the type is 'chip', all other props will be provided to the '<Chip>' component.
     *  If the type is 'field', all other props will be provided to the '<TextInput>' component when multiline={false} or
     *  to the '<TextwChipsContentEditable> when multiline={true}.
     *
     * `selected`: Set `true` if the chips is selected.
     * `listFocused`: Set `true` if the focus is on the autocomplete list
     * `focused`: Set `true` if the item is focused.
     *
     * `contextMenu`: ContextMenu configuration used for copy and paste
     * `contextMenu.event`: The event toggling the menu, it is used to find the position of the pointer, omit this to render the list under the item.
     * `contextMenu.items`: List items configuration
     *
     * `autocomplete`: Autocomplete configuration used for suggesting chip values
     * `autocomplete.event`: The event toggling the menu, it is used to find the position of the pointer, omit this to render the list under the item.
     * `autocomplete.items`: List items configuration
     */
  data: {
    id: string,
    value: string,
    placeholder?: string,
    type?: 'field' | 'chip' | 'textforchip',
    selected?: boolean,
    listFocused?: boolean,
    focused?: boolean,
    contextMenu?: {
      event?: {},
      items: {
        label: string,
        onClick?: React.MouseEventHandler
      }[]
    },
    autocomplete?: {
      event?: {},
      items: {
        label: string,
        onClick?: React.MouseEventHandler
      }[]
    }
  }[];
  /** If `true` the component gets focus after initial render. */
  autoFocus?: boolean;
  /** If true, place focus on this component. */
  focusItem?: boolean;
  /** The character that triggers Chip creation. */
  triggerCharacter?: string;
  /** Callback function with the updated `data` array. */
  onChange(...args: unknown[]): unknown;
  /** Display the component with an outline or underline style.  */
  variant?: 'outlined' | 'underlined';
  /** If true, allow the component to display more than one row. */
  multiline?: boolean;
  /** Specify resize of the height or the width of the container. Applies only when multiline=true. */
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  /** If `true`, the component will be disabled. */
  disabled?: boolean;
  /** If `true`, the field cannot be edited by the user. */
  readOnly?: boolean;
  /** If `true`, the input will be rendered in the error state. */
  error?: boolean;
  /** If set, the message will be displayed in the error state. `Typography` component can be used. */
  errorMessage?: React.ReactNode;
  /** If `true`, the max-width will be `31.25rem`. */
  maxWidth?: boolean;
  /** If `true`, render a clear button used to clear the input. */
  showClearButton?: boolean;
  /** Properties for the clear button. */
  clearButtonProps?: Partial<IconButtonProps>;
  /** Use a custom icon (to replace the default <CloseIcon />). See <IconButton /> and <Icon/> component for more information. */
  clearButtonIcon?: React.ReactNode;
  /** If `true`, render a close button on each Chip. */
  showChipCloseButton?: boolean;
  /** Use a custom icon (to replace the default <CloseIcon>) on each Chip. See <IconButton /> and <Icon/> component for more information. */
  chipCloseButtonIcon?: React.ReactNode;
  /** Properties for the main TextwChips wrapper. */
  wholeWrapperProps?: any;
  /** Properties for the children wrapper. */
  childWrapperProps?: any;
  /** Properties to pass to all `input` element(s). */
  inputProps?: React.ComponentPropsWithRef<'input'>,
  /** Dropdown List properties. See List component documentation for details. */
  listProps?: Partial<TextwChipsListProps>;
  /** Override autocomplete `Menu` positioning options based on popper (https://popper.js.org/docs/v2/). */
  menuProps?: Partial<MenuProps>;
  /** to configure 'aria-describedby' attribute. */
  ariaDescribedBy?: string;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
  /** @ignore */
  onMouseDown?: React.MouseEventHandler;
  /** @ignore */
  onMouseUp?: React.MouseEventHandler;
}

/**
 * TextwChips, in non multiline mode, is a `TextInput` like component that allows Chips.
 * TextwChips in multiline mode is a `TextArea` like component that allows Chips.
 * Any other props than the `TextwChips` props will be provided to the root element.
 * */
const TextwChips = React.forwardRef<HTMLElement, TextwChipsProps>((props: TextwChipsProps, ref) => getContext(({ RTL, theme }) => {
  const {
    data,
    onChange,
    autoFocus,
    focusItem,
    disabled,
    readOnly,
    multiline,
    triggerCharacter,
    showChipCloseButton,
    chipCloseButtonIcon,
    childWrapperProps,
    wholeWrapperProps,
    menuProps,
    listProps,
    inputProps,
    resize,
    maxWidth,
    variant,
    ariaDescribedBy,
    onFocus,
    onBlur,
    onMouseDown,
    onMouseUp,
    ...otherProps
  } = props;
  const { style: propStyle, ...restOfOtherProps } = otherProps;
  const [focused, setFocusState] = useState(false);
  const lastFieldEndPosition = useRef(0);
  const refs = useRef<any>(data.map(() => createRef()));
  const listRef = useRef(null);
  const [textwChipsCoreRef, { width: wrapperWidth, left: wrapperLeft }] = useMeasure({ polyfill: ResizeObserver });

  const combinedRef = useCombinedRefs(textwChipsCoreRef, ref);
  const clickHandle = useRef<0 | ReturnType<typeof setTimeout>>(0);
  const getLastComponentIndex = () => refs.current.length - 1 < 0 ? 0 : refs.current.length - 1;

  // useFocusManager hook provides a way to track and manage focus as it moves around a
  // container element. In this case the Chip, TextwChipsInput, TextwChipsContentEditable are all
  // inside the TextwChips component and get focus individually, but the TextwChips component
  // needs to show the focus state when the inside elements are focused and not blur (unfocus) until
  // all container elements are out of focus.
  const { onBlur: onContainerBlur, onFocus: onContainerFocus } = useFocusManager({
    isDisabled: () => false,
    onChange: (nextFocused) => {
      setFocusState(nextFocused);
    }
  });

  // when this component gets autoFocus, the last component in the container must get the focus.
  useEffect(() => {
    if (autoFocus) {
      const focusIndex = getLastComponentIndex();
      refs.current[focusIndex] && refs.current[focusIndex].current && (refs.current[focusIndex].current as HTMLElement).focus();
    }
  }, []);

  const focusOnIndex = (targetItemIndex, noListFocused = false) => {
    onChange(data.map((item, index) => ({
      ...item,
      focused: index === targetItemIndex,
      listFocused: noListFocused ? false : item.listFocused
    })));
  };

  // focus on the last component in the container when the container is focused.
  useDidUpdateEffect(() => {
    // focus on the last field/chip when this component gets initial focus
    if (focused) {
      const focusIndex = getLastComponentIndex();
      refs.current[focusIndex] && refs.current[focusIndex].current && (refs.current[focusIndex].current as HTMLElement).focus();
    }
  }, [focused]);

  // when focusItem property is true, focus on the last component in the container. This property
  // is used when the ClearButton is pressed to give focus to back to the main component and also
  // when there is a label with a help icon that must arrow down and give focus to the main component.
  useEffect(() => {
    // focus on last field of component (useful for ClearButton)
    if (focusItem) {
      const focusIndex = getLastComponentIndex();
      focusOnIndex(focusIndex, false);
    }
  }, [focusItem]);

  /* Focus on an item (chip or field) whenever new data is received. */
  useDidUpdateEffect(() => {
    const focusIndex = data.findIndex(item => item.focused);
    if (focusIndex > -1) {
      refs.current[focusIndex]
      && refs.current[focusIndex].current
      && (refs.current[focusIndex].current as HTMLElement).focus();
    }
    // use lastFieldEndPosition to calculate width of last component in multiline variant
    if (multiline && data.length >= 2) {
      const lastFieldRef = refs.current[data.length - 2];
      lastFieldEndPosition.current = lastFieldRef && lastFieldRef.current !== null ? (lastFieldRef.current as HTMLElement).getBoundingClientRect().right : 0;
    }
  }, [
    data.findIndex(item => item.focused),
    data.length,
    data.filter(item => item.type === 'field').length,
    data.filter(item => item.type === 'chip').length,
    data.filter(item => item.type === 'textforchip').length
  ]);

  // Focus on the list.
  useEffect(() => {
    listRef
    && listRef.current
    && listRef.current.focus();
  }, [
    data.findIndex(item => item.listFocused)
  ]);

  const isEmptyField = (targetIndex) => {
    let isEmpty = false;
    if (targetIndex > 0 && targetIndex < data.length) {
      const targetItem = data[targetIndex];
      if (targetItem.type === 'field' && targetItem.value === EMPTY_STRING) {
        isEmpty = true;
      }
    }
    return isEmpty;
  };

  /** Remove a single chip and any unnecessary empty field, update the focus index. */
  const removeChip = (id) => {
    const targetIndex = getIndexById(data, id);
    const focusedIndexAfterRemoval = targetIndex - 1;
    const focusedUpdatedData = data.map((item, index) => ({
      ...item,
      focused: index === focusedIndexAfterRemoval
    }));
    const hasEmptyPreviousField = isEmptyField(targetIndex - 1);
    const hasEmptyNextField = isEmptyField(targetIndex + 1);
    if (hasEmptyPreviousField) {
      focusedUpdatedData.splice(targetIndex - 1, 2);
    } else if (hasEmptyNextField) {
      focusedUpdatedData.splice(targetIndex, 2);
    } else {
      focusedUpdatedData.splice(targetIndex, 1);
    }
    return focusedUpdatedData;
  };

  /** Remove selected and the focused chip, and their next fields, update the focus index. */
  const removeChips = (id) => {
    const focusedChipIndex = getIndexById(data, id);
    let focusedIndexAfterRemoval = focusedChipIndex;
    for (let index = focusedChipIndex; index > 0; index -= 1) {
      if (!data[index - 1].selected) {
        focusedIndexAfterRemoval = index - 1;
        break;
      }
      if (index === 1) {
        focusedIndexAfterRemoval = 0;
      }
    }
    const updatedFocusedData = data.map((item, index) => ({
      ...item,
      focused: index === focusedIndexAfterRemoval
    }));
    const removedItemsData = updatedFocusedData.filter((dataItem, index) => {
      if (dataItem.id === id) {
        return false;
      }
      // remove the field that is located after the removed chip
      if (dataItem.type === 'field' && index > 0 && data[index - 1].id === id) {
        return false;
      }
      // remove selected chips
      if (dataItem.type === 'chip' && dataItem.selected) {
        return false;
      }
      // remove the field that is located after the selected chips
      if (dataItem.type === 'field' && index > 0 && data[index - 1].selected) {
        return false;
      }
      return true;
    });
    onChange(removedItemsData);
  };

  // convert the text in the field to a Chip and add an EMPTY field after the Chip when necessary
  const convertFieldToChip = (id, index: number, value) => {
    const targetIndex = index === undefined ? getIndexById(data, id) : index;
    const isNextField = data[targetIndex + 1] && data[targetIndex + 1].type === 'field';
    const isPreField = data[targetIndex - 1] && data[targetIndex - 1].type === 'field';
    const modifiedData = data.map(dataItem => dataItem.id === id ? {
      ...dataItem,
      value: value === undefined ? dataItem.value : value,
      type: 'chip',
      selected: false,
      focused: false,
    } : {
      ...dataItem,
      focused: false
    });
    if (!isNextField) {
      modifiedData.splice(targetIndex + 1, 0, {
        focused: true,
        type: 'field',
        id: `field-${_uniqueId()}`,
        value: EMPTY_STRING
      });
    } else {
      // focus the field after the newly added Chip
      modifiedData[targetIndex + 1].focused = true;
    }
    if (!isPreField) {
      modifiedData.splice(targetIndex, 0, {
        focused: false,
        type: 'field',
        id: `field-${_uniqueId()}`,
        value: EMPTY_STRING
      });
    }
    return modifiedData;
  };

  // a value has been selected from the autocomplete menu list */
  const submitAutocompleteItem = ({ label, id, index }) => {
    onChange(convertFieldToChip(id, index, label));
  };

  // change the type of the data from 'chip' to 'field' for editing
  const convertChipToField = (id) => {
    const modifiedData = data.map(dataItem => dataItem.id === id ? {
      ...dataItem,
      type: 'field',
      focused: true
    } : {
      ...dataItem,
      focused: false
    });
    return modifiedData;
  };

  // triggerCharacter entered, start creating a Chip
  const convertFieldToChipCreation = (id, cPosition, addTrigger) => {
    // change all focus to false
    const newData = data.map(dataItem => ({
      ...dataItem,
      focused: false
    }));
    const targetItemIndex = getIndexById(newData, id);
    if (cPosition === 0) { // adding Chip to beginning of field
      // add 'textforchip' Chip creation indicator
      const newItem = {
        // type: 'textforchip', value: EMPTY_STRING, id: `field-${_uniqueId()}`, focused: true
        type: 'textforchip' as const,
        value: addTrigger ? triggerCharacter : EMPTY_STRING,
        id: `field-${_uniqueId()}`,
        focused: true
      };
      // if this is the first data item remove placeholder value
      if (newData.length === 1) {
        newData[0] = { ..._omit(newData[0], 'placeholder') };
      }
      newData.splice(targetItemIndex + 1, 0, newItem);
    } else {
      const targetItem = newData[targetItemIndex];
      const triggerItemValue = targetItem.value;
      // split the string into 2 'field' types, new 'textforchip' in the middle of those
      const firstStringPart = triggerItemValue.substr(0, cPosition - 1);
      const secondStringPart = triggerItemValue.substr(cPosition);
      newData[targetItemIndex].value = firstStringPart; // update existing data 'field'
      // add textforchips
      const newTextForChipItem = {
        type: 'textforchip' as const,
        value: addTrigger ? triggerCharacter : EMPTY_STRING,
        id: `field-${_uniqueId()}`,
        focused: true
      };
      // add last part of text 'field'
      const newTextField = {
        type: 'field' as const,
        value: secondStringPart,
        id: `field-${_uniqueId()}`,
        focused: false
      };
      newData.splice(targetItemIndex + 1, 0, newTextForChipItem, newTextField);
    }
    return newData;
  };

  // editing current Chip
  const convertChipToChipCreation = (id) => {
    // change other focus to false
    const newData = data.map(dataItem => ({
      ...dataItem,
      focused: false
    }));
    const targetItemIndex = getIndexById(newData, id);
    // add 'textforchip' Chip creation indicator
    const targetValue = newData[targetItemIndex].value;
    newData[targetItemIndex].value = triggerCharacter + targetValue;
    newData[targetItemIndex].type = 'textforchip';
    newData[targetItemIndex].focused = true;
    return newData;
  };

  const handleFieldKeyDown = (eventData, { id }) => {
    let hadAction = false;
    // get the dataValue from data and the cursor position from position library due to
    // the TextwChipsContentEditable not returning 'normal' input field event
    const dataIndex = getIndexById(data, id);
    const dataValue = (dataIndex > -1) && (dataIndex < data.length) ? data[dataIndex].value : undefined;
    const dataRef = refs.current[dataIndex] && refs.current[dataIndex].current;
    let cPosition = 0;
    const contentEditable = (dataRef as HTMLElement).getAttribute('contentEditable');
    const tagName = (dataRef as HTMLElement).tagName.toLowerCase();
    let isEditable = true;
    if (contentEditable === null && tagName === 'div') {
      isEditable = false;
    }
    // non-editable div's do not have a caret, therefore do not use position()
    if (isEditable && dataRef) {
      const { pos } = position((dataRef as HTMLElement));
      cPosition = pos;
    }
    // Focus on pre item
    if (eventData.key === ARROW_LEFT && cPosition === 0) {
      const targetItemIndex = getIndexById(data, id) - 1;
      targetItemIndex > -1 && focusOnIndex(targetItemIndex, true);
    }

    // Focus on next item
    if (eventData.key === ARROW_RIGHT && dataValue !== undefined && (cPosition > dataValue.length - 1)) {
      const targetItemIndex = getIndexById(data, id) + 1;
      targetItemIndex < data.length && focusOnIndex(targetItemIndex, true);
    }

    // Focus on the autocomplete list
    if (eventData.key === ARROW_DOWN) {
      if (listRef && listRef.current) {
        eventData.preventDefault();
        onChange(data.map(item => ({ ...item, listFocused: item.id === id })));
        hadAction = true;
      }
    }

    // Submit the chip
    if (eventData.key === ENTER_KEY) {
      const targetItemIndex = getIndexById(data, id);
      const valueType = data[targetItemIndex].type;
      if (valueType === 'textforchip') {
        // remove trigger character
        const replacedValue = data[targetItemIndex].value.replace(triggerCharacter, '');
        const newData = convertFieldToChip(id, targetItemIndex, replacedValue);
        onChange(newData);
        hadAction = true;
      }
    }

    // ESCAPE cancels Chip creation. Add the text as a field.
    if (eventData.key === ESCAPE) {
      const targetItemIndex = getIndexById(data, id);
      const valueType = data[targetItemIndex].type;
      if (valueType === 'textforchip') {
        const newData = convertChipToField(id);
        onChange(newData);
        hadAction = true;
      }
    }

    // Remove pre chip
    if (eventData.key === BACKSPACE && dataValue === EMPTY_STRING) {
      const targetIndex = getIndexById(data, id) - 1;
      if (targetIndex > 0) {
        onChange(removeChip(data[targetIndex].id));
        hadAction = true;
      }
    }

    // Remove next chip
    if (eventData.key === DELETE && dataValue === EMPTY_STRING) {
      const targetIndex = getIndexById(data, id) + 1;
      if (targetIndex < data.length) {
        onChange(removeChip(data[targetIndex].id));
        hadAction = true;
      }
    }

    // Start Chip creation
    if (eventData.key === triggerCharacter) {
      const targetIndex = getIndexById(data, id);
      if (targetIndex > -1) {
        const targetValue = data[targetIndex].value;
        const lastCharacter = cPosition === 0 ? targetValue.substr(targetValue.length - 1, 1) : targetValue.substr(cPosition - 1, 1);
        const lastCharacterAsciiCode = cPosition === 0 ? targetValue.charCodeAt(targetValue.length - 1) : targetValue.charCodeAt(cPosition - 1); // ContentEditable has ASCII for blank char
        // only start Chip creation if the triggerCharacter is preceded by a blank or it's the first char in the value string
        if (lastCharacter === BLANK_STRING || lastCharacterAsciiCode === BLANK_ASCII || targetValue.length === 0) {
          const addTrigger = (lastCharacterAsciiCode !== BLANK_ASCII) || !multiline; // again specific for ContentEditable
          onChange(convertFieldToChipCreation(id, cPosition, addTrigger)); // need caret position in case Chip is inserted inside existing text field
          if (addTrigger) {
            hadAction = true;
          }
        }
      }
    }

    return hadAction;
  };

  const selectChip = (id) => {
    onChange([...data.map(dataItem =>
      dataItem.type === 'chip'
        ? dataItem.id === id ? { ...dataItem, selected: !dataItem.selected } : { ...dataItem, selected: false }
        : dataItem)
    ]);
  };

  const addShiftKeyChip = (id) => {
    onChange([...data.map(dataItem =>
      dataItem.type === 'chip'
        ? dataItem.id === id ? { ...dataItem, selected: !dataItem.selected } : dataItem
        : dataItem)
    ]);
  };

  const handleSingleChipClick = () => {
    clickHandle.current && clearTimeout(clickHandle.current);
    clickHandle.current = 0;
  };

  const setSingleClickChip = () => {
    clickHandle.current = setTimeout(() => {
      handleSingleChipClick();
    }, 250);
  };

  const handleDoubleChipClick = (id) => {
    clickHandle.current && clearTimeout(clickHandle.current);
    clickHandle.current = 0;
    const newData = convertChipToChipCreation(id);
    onChange(newData);
  };

  const handleChipClick = (eventData, id) => {
    if (eventData.shiftKey) {
      addShiftKeyChip(id);
      return;
    }
    if (clickHandle.current) {
      handleDoubleChipClick(id);
      return;
    }
    selectChip(id);
    setSingleClickChip();
  };

  const handleChipKeyDown = (eventData, id) => {
    // Focus on pre item
    if (eventData.key === ARROW_LEFT) {
      const targetItemIndex = getIndexById(data, id) - 1;
      targetItemIndex > -1 && focusOnIndex(targetItemIndex, true);
    }

    // Focus on next item
    if (eventData.key === ARROW_RIGHT) {
      const targetItemIndex = getIndexById(data, id) + 1;
      targetItemIndex < data.length && focusOnIndex(targetItemIndex, true);
    }

    // Focus on the contextmenu list
    if (eventData.key === ARROW_DOWN) {
      if (listRef && listRef.current) {
        eventData.preventDefault();
        onChange(data.map(item => ({ ...item, listFocused: item.id === id })));
      }
    }

    if (eventData.key === BACKSPACE || eventData.key === DELETE) {
      eventData.nativeEvent.preventDefault();
      eventData.nativeEvent.stopPropagation();
      removeChips(id);
      return;
    }
    if (eventData.key === ENTER_KEY && eventData.shiftKey) {
      eventData.nativeEvent.preventDefault();
      addShiftKeyChip(id);
      return;
    }
    if (eventData.key === ENTER_KEY) {
      if (clickHandle.current) {
        handleDoubleChipClick(id);
        return;
      }
      selectChip(id);
      setSingleClickChip();
    }
  };

  const handleAutoCompleteKeydown = (event, { id, index, label }) => {
    if (event.key === ENTER_KEY || event.key === SPACE_KEY) {
      event.preventDefault();
      event.stopPropagation();
      submitAutocompleteItem({ id, index, label });
    }
  };

  const ariaString = () => {
    let aString = '';
    for (let ind = 0; ind < data.length; ind += 1) {
      if (data[ind].type !== 'textforchip') {
        aString += `${data[ind].value} `;
      }
    }
    return aString;
  };

  const handleMenuClose = (id) => {
    // menu closed, do not add text as a Chip
    const newData = convertChipToField(id);
    onChange(newData);
  };

  const handleTextwChipsBlur = (event) => {
    onContainerBlur(event);
    onBlur && onBlur(event);
  };

  const handleTextwChipsFocus = (event) => {
    onContainerFocus(event);
    onFocus && onFocus(event);
  };

  // to help determine menu max height, multiline will pass a height.
  const { style: childStyle } = childWrapperProps;
  const { height: childHeight } = childStyle || {};
  const contentHeight = childHeight || SINGLE_LINE_HEIGHT;

  const renderChip = (item, index) => {
    const {
      id,
      value,
      selected,
      onClick,
      onKeyDown,
      onBlur: onChipBlur,
      onFocus: onChipFocus,
      contextMenu,
      theme: chipTheme,
      ...otherChipProps
    } = item;
    const { style: chipPropsStyle, ...restOfChipProps } = otherChipProps;
    const chipProps = {
      id,
      value,
      selected,
      size: 'small',
      disabled,
      theme: chipTheme,
      onClick: (eventData) => {
        if (!disabled && !readOnly) {
          handleChipClick(eventData, id);
          onClick && onClick(eventData);
        }
      },
      onKeyDown: (eventData) => {
        if (!disabled && !readOnly) {
          handleChipKeyDown(eventData, id);
          onKeyDown && onKeyDown(eventData);
        }
      },
      onBlur: (eventData) => {
        if (!disabled && !readOnly) {
          item.focused === index && focusOnIndex(-1, true);
          onChipBlur && onChipBlur(eventData);
        }
      },
      onFocus: (eventData) => {
        if (!disabled && !readOnly) {
          focusOnIndex(index, true);
          onChipFocus && onChipFocus(eventData);
        }
      },
      'aria-label': selected ? `${(value as string)} selected, press Enter twice to edit.` : `${(value as string)}, press Enter twice to edit.`,
      'aria-describedby': index === data.length - 1 ? ariaDescribedBy : undefined,
      ref: refs.current[index],
      tabIndex: (disabled || readOnly) ? -1 : 0,
      ..._merge({}, CHIP_CONFIG, restOfChipProps),
      style: {
        margin: RTL ? `0 0 0 ${SPACING_4}` : `0 ${SPACING_4} 0 0`,
        flexShrink: isFirefox && !multiline ? 0 : undefined, // Chip RippleEffect in FF non muiltiline will shrink chip w/o this
        ...chipPropsStyle,
      }
    };
    return (
      <React.Fragment key={id}>
        <Chip {...chipProps}>
          <ChipLabel label={item.value} />
          {showChipCloseButton &&
            <ChipIconButton
              tabIndex={(disabled || readOnly) ? -1 : 0}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (!disabled && !readOnly) {
                  const newData = removeChip(item.id);
                  onChange(newData);
                }
              }}
            >
              {chipCloseButtonIcon}
            </ChipIconButton>
          }
        </Chip>
        <TextwChipsList
          menuProps={menuProps}
          {...{
            list: contextMenu,
            triggerEl: refs.current[index],
            listRef,
            contentHeight,
          }}
        />
      </React.Fragment>
    );
  };

  const renderTextForChip = (item, index) => {
    const {
      id,
      contextMenu,
      autocomplete,
      onKeyDown,
      focused: inputFocused,
      theme: itemTheme,
      ...otherFieldProps
    } = item;
    const autocompleteItems = autocomplete && autocomplete.items.map(autocompleteItem => ({
      ...autocompleteItem,
      onMouseDown: (event) => {
        event.preventDefault();
        event.stopPropagation();
        submitAutocompleteItem({ id: item.id, index, label: autocompleteItem.label });
        autocompleteItem.onMouseDown && autocompleteItem.onMouseDown(event);
      },
      onKeyDown: (event) => {
        if (event.key === ESCAPE) {
          // Escape closes Menu
          focusOnIndex(index, true);
        } else {
          handleAutoCompleteKeydown(event, { id: item.id, index, label: autocompleteItem.label });
          autocompleteItem.onKeyDown && autocompleteItem.onKeyDown(event);
        }
      },
    }));
    const resolvedInputProps = {
      onFocus: () => { focusOnIndex(index, true); },
      onBlur: () => { inputFocused && focusOnIndex(-1); },
      tabIndex: (disabled || readOnly) ? -1 : 0,
      ref: refs.current[index],
      theme: _merge({}, theme.TEXT_INPUT, itemTheme),
      disabled,
      readOnly,
      onChange: (eventData) => {
        onChange(data.map(dataItem => dataItem.id === item.id ? { ...dataItem, value: eventData.target.value } : dataItem));
      },
      onKeyDown: (eventData) => {
        const hadAction = handleFieldKeyDown(eventData, { id });
        // do not let the onKeyDown trigger onChange if the onKeyDown took action
        hadAction && eventData.preventDefault();
        onKeyDown && onKeyDown(eventData);
      }
    };
    return (
      <TextwChipsHighlighter
        key={id}
        triggerCharacter={triggerCharacter}
        aria-label={`${item.value as string}, press Arrow Down key to reach autocomplete list`}
        {...item}
        {...resolvedInputProps}
        {...otherFieldProps}
        listProps={{
          ...listProps,
          menuProps,
          contextMenu,
          autocomplete: autocomplete ? { ...autocomplete, items: autocompleteItems } : undefined,
          triggerEl: refs.current[index],
          listRef,
          contentHeight,
          onClose: () => { handleMenuClose(id); },
        }}
      />
    );
  };

  const renderContentEditable = (item, index) => {
    const {
      id,
      contextMenu,
      onChange: fieldOnChange,
      onKeyDown,
      focused: inputFocused,
      theme: itemTheme,
      ...otherFieldProps
    } = item;
    const commonInputProps = {
      tabIndex: (disabled || readOnly) ? -1 : 0,
      ref: refs.current[index],
      onFocus: () => { focusOnIndex(index, true); },
      onBlur: () => { inputFocused && focusOnIndex(-1); },
    };

    return (
      <TextwChipsContentEditable
        key={id}
        id={item.id}
        variant={variant}
        value={item.value}
        disabled={disabled}
        placeholder={item.placeholder}
        theme={_merge({}, theme.TEXT_INPUT, itemTheme)}
        containerWidth={wrapperWidth + wrapperLeft}
        lastFieldEndPosition={lastFieldEndPosition.current + wrapperLeft}
        lastInput={index === data.length - 1}
        firstInput={index === 0 && !data[0].placeholder}
        aria={{
          'aria-label': index === data.length - 1 ? `${ariaString()}` : item.value,
          'aria-describedby': index === data.length - 1 ? ariaDescribedBy : undefined,
        }}
        {...otherFieldProps}
        {...commonInputProps}
        onKeyDown={(eventData) => {
          const hadAction = handleFieldKeyDown(eventData, { id });
          // do not let the onKeyDown trigger onChange if the onKeyDown took action
          hadAction && eventData.preventDefault();
          onKeyDown && onKeyDown(eventData);
        }}
        onChange={(newValue) => {
          onChange(data.map(dataItem => dataItem.id === item.id ? { ...dataItem, value: newValue } : dataItem));
          fieldOnChange && fieldOnChange(newValue);
        }}
        listProps={{
          menuProps,
          contextMenu,
          triggerEl: refs.current[index],
          listRef,
          contentHeight,
          ...listProps,
        }}
      />);
  };

  const renderInput = (item, index) => {
    const {
      id,
      contextMenu,
      onChange: fieldOnChange,
      onKeyDown,
      focused: inputFocused,
      theme: itemTheme,
      ...otherFieldProps
    } = item;
    return (
      <React.Fragment key={id} >
        <TextwChipsInput
          key={id}
          variant={variant}
          theme={itemTheme}
          disabled={disabled}
          readOnly={readOnly}
          {...otherFieldProps}
          inputProps={{
            id,
            onFocus: () => { focusOnIndex(index, true); },
            onBlur: () => { inputFocused && focusOnIndex(-1); },
            tabIndex: (disabled || readOnly) ? -1 : 0,
            ref: refs.current[index],
            'aria-describedby': index === data.length - 1 ? ariaDescribedBy : undefined,
            'aria-label': index === data.length - 1 ? `${ariaString()}` : item.value,
            ...inputProps
          }}
          lastInput={index === data.length - 1}
          onKeyDown={(eventData) => {
            const hadAction = handleFieldKeyDown(eventData, { id });
            // do not let the onKeyDown trigger onChange if the onKeyDown took action
            hadAction && eventData.preventDefault();
            onKeyDown && onKeyDown(eventData);
          }}
          onChange={(eventData) => {
            onChange(data.map(dataItem => dataItem.id === id ? { ...dataItem, value: (eventData.target as HTMLInputElement).value } : dataItem));
            fieldOnChange && fieldOnChange(eventData);
          }}
          listProps={{
            menuProps,
            contextMenu,
            triggerEl: refs.current[index],
            listRef,
            contentHeight,
            ...listProps,
          }}
        />
      </React.Fragment>
    );
  };

  refs.current = data.map(() => createRef());
  const resolvedWrapperProps = childWrapperProps;
  const resolvedWholeWrapperProps = _merge({}, { maxWidth }, wholeWrapperProps);
  const isComponentEmpty = data.length === 1 && data[0].value === EMPTY_STRING;

  return (
    <TextwChipsContext.Provider value={{ ...props }}>
      <TextwChipsCore
        ref={combinedRef}
        RTL={RTL}
        tabIndex={0}
        focused={focused}
        variant={variant}
        disabled={disabled}
        readOnly={readOnly}
        resize={multiline && (resize !== DEFAULT_RESIZE_MODE)}
        wrapperProps={resolvedWrapperProps}
        wholeWrapperProps={resolvedWholeWrapperProps}
        multiline={multiline}
        style={(resize === DEFAULT_RESIZE_MODE || !multiline || disabled) ? propStyle : { ...propStyle, resize }}
        isEmpty={isComponentEmpty}
        {...restOfOtherProps}
        onFocus={handleTextwChipsFocus}
        onBlur={handleTextwChipsBlur}
        onMouseDown={(e) => { onMouseDown && onMouseDown(e); }}
        onMouseUp={(e) => { onMouseUp && onMouseUp(e); }}
      >
        {data.map((item, index) => {
          if (item.type === 'chip') {
            return renderChip(item, index);
          }
          if (item.type === 'textforchip') {
            return renderTextForChip(item, index);
          }
          if (multiline) {
            return renderContentEditable(item, index);
          }
          return renderInput(item, index);
        })}
      </TextwChipsCore>
    </TextwChipsContext.Provider>
  );
}));

TextwChips.displayName = 'TextwChips';

TextwChips.defaultProps = {
  autoFocus: false,
  focusItem: false,
  triggerCharacter: '@',
  variant: 'outlined',
  multiline: false,
  resize: DEFAULT_RESIZE_MODE,
  disabled: false,
  readOnly: false,
  error: false,
  errorMessage: undefined,
  maxWidth: false,
  showClearButton: false,
  clearButtonProps: {},
  clearButtonIcon: undefined,
  showChipCloseButton: false,
  chipCloseButtonIcon: <CloseIcon />,
  wholeWrapperProps: {},
  childWrapperProps: {},
  inputProps: {},
  listProps: {},
  menuProps: {
    usePortal: true,
    portalContainer: document.body,
  },
  ariaDescribedBy: undefined,
  onFocus: undefined,
  onBlur: undefined,
  onMouseDown: undefined,
  onMouseUp: undefined
};

export {
  TextwChips as default,
  TextwChipsContext,
};
